import {
  Button,
  CircularProgress,
  FormLabel,
  makeStyles,
  Typography
} from '@material-ui/core'
import i18n from 'i18next'
import { FC } from 'react'
import { connect, MapStateToProps } from 'react-redux'
import { Field, InjectedFormProps, reduxForm } from 'redux-form'
import { ReduxPhoneField } from '../../components/FormInput/ReduxWrappers'
import { selectCreateApplication } from '../../selectors/applications'
import { StateInterface } from '../../types/states'
import { MyInput, MyTextArea } from './Fields'
import { CheckboxField } from './Fields/CheckboxField'

const useStyles = makeStyles({
  formRoot: {
    textAlign: 'center',
    maxWidth: '500px',
    padding: '0 20px',
    margin: '20px auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    '& label': {
      fontSize: '12px',
      fontWeight: '500',
      color: 'black'
    }
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '20px'
  },
  fieldItem: {
    textAlign: 'left'
  },
  fullWidthField: {
    textAlign: 'left'
  },
  phoneInput: {
    textAlign: 'left',
    '& .react-tel-input': {
      backgroundColor: 'rgb(243, 243, 243) !important',
      minHeight: '18px',
      borderRadius: '6px !important',
      fontSize: '13px',
      border: '1px solid rgb(232, 232, 232)',
      '&::after': {
        content: 'none !important'
      },
      '&::before': {
        content: 'none !important'
      },
      '&:hover': {
        marginBottom: '1px'
      },
      '& .special-label': {
        background: 'transparent',
        color: 'black',
        fontSize: '12px',
        fontWeight: '500',
        top: '-20px',
        fontFamily: 'CircularRegular'
      },
      '& input': {
        position: 'relative',
        zIndex: 2,
        background: 'transparent',
        fontFamily: 'CircularRegular'
      },
      '& .flag-dropdown': {
        zIndex: 2,
        background: 'transparent !important',
        backgroundColor: 'transparent !important',
        '& .selected-flag': {
          background: 'transparent !important',
          backgroundColor: 'transparent !important',
          '& .flag': {
            transform: 'scale(1.2) translateY(-1px)',
            '& .arrow': {
              transform: 'scale(1)'
            }
          }
        }
      }
    }
  },
  notesWrapper: {
    textAlign: 'left'
  },
  checkboxWrapper: {
    textAlign: 'left'
  },
  superLikeBox: {
    backgroundColor: '#FBF6EE',
    borderRadius: '10px',
    padding: '12px 16px',
    marginBottom: '12px',
    border: '1px solid #F1D9C5'
  },
  superLikeTitle: {
    display: 'block',
    color: '#1D1C1D',
    fontWeight: 700,
    fontSize: '13px',
    marginBottom: '2px'
  },
  superLikeDescription: {
    color: 'rgb(102, 102, 102)',
    fontSize: '12px',
    lineHeight: 1.4
  },
  checkboxIndented: {
    marginLeft: '16px',
    marginTop: '-10px',
  },
  submitButton: {
    width: '100%',
    backgroundColor: '#94004A',
    padding: '5px 45px',
    color: 'white',
    borderRadius: '6px',
    textTransform: 'none',
    fontWeight: 'bold',
    alignSelf: 'center',
    '& div': {
      width: '20px !important',
      height: '20px !important'
    },
    '& svg': {
      color: 'white'
    },
    '&:hover': {
      backgroundColor: '#670033 !important',
      color: '#FFFFFF'
    }
  },
  errorMessage: {
    fontSize: '0.75rem',
    marginTop: '5px'
  },
  formLabel: {
    display: 'inline-block',
    paddingBottom: '0.4rem'
  }
})

type MinimalFormValues = {
  firstName: string
  lastName: string
  email: string
  phone: { phone: string; countryDialCode: string }
  notes?: string
  agreed: boolean
  superLike?: boolean
  contactInformationConsent?: boolean
}

interface OwnProps {
  onSubmit: (values: MinimalFormValues) => Promise<any> | void
  initialValues: Partial<MinimalFormValues>
}

interface StateProps {
  loading: boolean
  _error: string | null
}

type Props = OwnProps &
  StateProps &
  InjectedFormProps<MinimalFormValues, OwnProps & StateProps>

const required = (value?: string | boolean) =>
  value ? undefined : i18n.t('validation.required')

type PhoneFieldValue = { phone?: string; countryDialCode?: string }

const phoneRequired = (value?: PhoneFieldValue) => {
  if (value?.phone?.length === 3) {
    return i18n.t('validation.required')
  }
  return undefined
}

const minPhoneLength = (value?: PhoneFieldValue) => {
  if (value && value.phone && value.phone.length >= 10) {
    return undefined
  }
  return i18n.t('validation.invalidPhoneNumber')
}

const noZeroAfterCode = (value?: PhoneFieldValue) => {
  if (
    value &&
    value.phone &&
    (value.phone.startsWith(`${value.countryDialCode}0`) ||
      value.phone.startsWith(`+${value.countryDialCode}0`))
  ) {
    return i18n.t('validation.phoneNumber')
  }
  return undefined
}

const t = (key: string) => i18n.t(`newApplication.${key}`)

const MinimalForm: FC<Props> = ({
  handleSubmit,
  loading,
  _error,
  onSubmit
}) => {
  const classes = useStyles()

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
      <div className={classes.formRoot}>
          <Typography
            variant='h5'
            style={{ textAlign: 'left', marginBottom: '16px' }}
          >
            {t('tellTheLandlordAboutYourself')}
          </Typography>
          <div className={classes.row}>
            <div className={classes.fieldItem}>
              <Field
                label={t('firstName')}
                name='firstName'
                component={MyInput}
                type='text'
                style={{ width: '100%' }}
                validate={[required]}
              />
            </div>
            <div className={classes.fieldItem}>
              <Field
                label={t('lastName')}
                name='lastName'
                component={MyInput}
                type='text'
                style={{ width: '100%' }}
                validate={[required]}
              />
            </div>
          </div>
          <div className={classes.fullWidthField}>
            <Field
              label={t('email')}
              name='email'
              component={MyInput}
              type='email'
              style={{ width: '100%' }}
              validate={[required]}
            />
          </div>
          <div className={classes.phoneInput}>
            <Field
              label={t('phone')}
              name='phone'
              component={ReduxPhoneField}
              placeholder='501234567'
              type='phone'
              style={{ width: '100%' }}
              validate={[phoneRequired, noZeroAfterCode, minPhoneLength]}
              isError
            />
          </div>
          <div className={classes.notesWrapper}>
            <FormLabel className={classes.formLabel}>{t('notes')}</FormLabel>
            <Field
              name='notes'
              component={MyTextArea}
              style={{ width: '100%', minHeight: '108px' }}
            />
          </div>
          {/* SuperLike temporarily disabled
          <div className={`${classes.checkboxWrapper} ${classes.superLikeBox}`}>
            <Field
              label={
                <div>
                  <div className={classes.superLikeTitle}>{t('superLikeLabel')}</div>
                  <div className={classes.superLikeDescription}>{t('superLikeDescription')}</div>
                </div>
              }
              name='superLike'
              component={CheckboxField}
            />
          </div>
          */}
          <div className={classes.checkboxWrapper}>
            <Field
              label={
                <>
                  {t('agreed')}{' '}
                  <a
                    href='https://leea.fi/tietosuojaseloste/'
                    target='_blank'
                    rel='noreferrer'
                  >
                    {t('privacyPolicy')}
                  </a>
                </>
              }
              name='agreed'
              component={CheckboxField}
              className={classes.checkboxIndented}
              validate={[required]}
            />
          </div>
          <Button
            type='submit'
            className={classes.submitButton}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={14} color='secondary' />
            ) : (
              i18n.t('general.send')
            )}
          </Button>
        {_error && (
          <Typography
            className={classes.errorMessage}
            align='center'
            color='error'
            variant='body2'
          >
            {_error}
          </Typography>
        )}
      </div>
    </form>
  )
}

const mapState: MapStateToProps<StateProps, OwnProps, StateInterface> = (
  state
) => {
  const createApplicationState = selectCreateApplication(state)
  return {
    loading: createApplicationState.loading,
    _error: createApplicationState.error
  }
}

const MinimalNewApplicationForm = connect<
  StateProps,
  object,
  OwnProps,
  StateInterface
>(mapState)(
  reduxForm<MinimalFormValues, OwnProps & StateProps>({
    form: 'newApplicationMinimal',
    enableReinitialize: true
  })(MinimalForm)
)

export default MinimalNewApplicationForm
